import React from 'react';
import SignOut from '../settings/sign_out';
import { MODAL } from '../settings/sign_out/sign_out';
import classes from './classes.module.scss';
import AppLink from '../routes/app_link';
import UserAvatarPicker from '../user_avatar_picker';
import ProfileInfoPopover from './profile_info_popover';
import HeaderSearch from './header_search';
import { getComponentStyle, componentStyles } from './component_styles';
import HeaderLogo from './header_logo';
import Const from '../../config/const';
import {
  matchBannerRoutes, getXfmFreeSetupMin, getXfmDefaultMinutes, isXFMActive, isPrerenderMode, formatLocalCurrency, generalCreditFormatForUSD, isDefaultCurrency
} from '../../config/util';
import { isPS } from '../../../common/config/utils';
import { useCurrentPath } from '../routes/hooks/useCurrentPath';
import TopBanner from '../../../common/top_banner';
import { SvgIcon } from '../../../common/sprites';
import { appRoutes } from '../../config/app_routes';

const defaultAvatar = require('./img/home_placeholder.png');
const plusIcon = require('./img/popover_plus_icon.svg');
const filledPlusIcon = require('./img/popover_filled_plus_icon.svg');
const editIcon = require('./img/popover_edit_icon.svg');

const getAvatar = ({ avatarUrlResize, avatarUrl }) => avatarUrlResize || avatarUrl || defaultAvatar;
const getAvatarIcon = ({ avatarUrlResize, avatarUrl }) => (avatarUrlResize || avatarUrl ? editIcon : plusIcon);

function AppHeader({
  badgeCount = 0,
  t,
  avatarUrl,
  avatarUrlResize,
  toggleDrawer,
  displayRegister,
  nickname,
  itemProp,
  localAvailableCredit = 0,
  clientAvailableCredit = '0',
  currency,
  bonusInfos,
  userBonusInfos,
  userXfmProgram,
  userId,
  showAddFundsMenuItem = false
}) {
  const { routeKey } = useCurrentPath();

  const getNicknameContainerClasses = () => {
    if (nickname) return classes.nicknameContainer;
    return `${ classes.nicknameContainer } ${ classes.empty }`;
  };

  const makeCurrentBallanceText = () => {
    if ((!localAvailableCredit || localAvailableCredit === 0) && showAddFundsMenuItem) { return t('header.addCredit'); }
    return isDefaultCurrency() || localAvailableCredit === 0 ? `${ t('header.credits') } ${ formatLocalCurrency(localAvailableCredit, currency) }` : `${ t('header.credits') } ~ ${ formatLocalCurrency(localAvailableCredit, currency) }`;
  };

  const getBalanceButtonClasses = () => {
    if (Number(clientAvailableCredit) < 0) return `${ classes.currentCreditAmountNegative } ${ classes.addCreditsButton }`;
    return classes.addCreditsButton;
  };

  const renderAddFundsButton = () => {
    const psNonDefaultCurrencyDisclaimer = isPS() && !isDefaultCurrency() && (
      <>
        <span className={ classes.generalCreditAmountUSD }>
          { generalCreditFormatForUSD(clientAvailableCredit) }
        </span>
        <div className={ classes.currencyCreditAmountDisclaimer }>{ t('header.local_currency_amounts_estimated') }</div>
      </>
    );

    if (showAddFundsMenuItem) {
      return (
        <>
          <AppLink to={ appRoutes.addFunds } className={ getBalanceButtonClasses() }>
            <span className={ classes.currentCreditAmount }>
              { makeCurrentBallanceText() }
            </span>
            <img src={ filledPlusIcon } alt={ t('header.addCredit') } />
          </AppLink>
          { (localAvailableCredit !== 0) && psNonDefaultCurrencyDisclaimer }
        </>
      );
    }

    return (
      <>
        <div className={ getBalanceButtonClasses() }>
          <span className={ classes.currentCreditAmount }>{ makeCurrentBallanceText() }</span>
        </div>
        { psNonDefaultCurrencyDisclaimer }
      </>
    );
  };

  function renderProfilePopoverHeader() {
    return (
      <div className={ classes.profilePopoverHeaderWrapper } suppressHydrationWarning>
        <div className={ classes.profilePopoverHeaderAvatarContainer }>
          <UserAvatarPicker>
            <div
              suppressHydrationWarning
              className={ classes.avatarContainer }
              style={ { '--size': '64px', '--radius':'24px' } }
            >
              <img
                suppressHydrationWarning
                alt=""
                src={ getAvatar({ avatarUrlResize, avatarUrl }) }
                style={ { '--radius': '20px' } }
                className={ classes.popoverAvatar }
              />
              <div className={ classes.avatarIconContainer }>
                <img className={ classes.avatarIcon } alt="" src={ getAvatarIcon({ avatarUrlResize, avatarUrl }) } />
              </div>
            </div>
          </UserAvatarPicker>
        </div>
        <div className={ classes.profilePopoverHeaderRightSide }>
          <div className={ getNicknameContainerClasses() } suppressHydrationWarning>
            { nickname || t('header.emptyNickname')}
          </div>
          { renderAddFundsButton() }
        </div>
      </div>
    );
  }

  function renderProfilePopoverContent() {
    return (
      <div className={ classes.popoverContainer }>
        <div>
          <AppLink to={ appRoutes.settings } className={ classes.popoverItem }>
            <SvgIcon id="popover_settings" size={ 24 } />
            <div className={ classes.popoverItemText }>{ t('sideMenu.settings') }</div>
          </AppLink>
        </div>
        <SignOut theme={ MODAL } />
      </div>
    );
  }

  const showSignUp = () => {
    displayRegister({ shouldDisplay: true, signUpFlow: 'menu', screen: Const.authScreens.signUp });
  };

  const showSignIn = () => {
    displayRegister({ shouldDisplay: true, signUpFlow: 'menu', screen: Const.authScreens.signIn });
  };

  const renderJoinButton = () => (
    <button
      onClick={ showSignUp }
      type="button"
      className={ classes.loginButtonContainer }
      style={ getComponentStyle(componentStyles.joinButton) }
      suppressHydrationWarning
    >
      {t('join_now')}
    </button>
  );

  function renderProfileInfo() {
    if (window.currentUserId) {
      return (
        <div className={ classes.popoverContainer } suppressHydrationWarning>
          <ProfileInfoPopover
            title={ renderProfilePopoverHeader() }
            content={ renderProfilePopoverContent() }
          >
            <div
              className={ classes.profileInfoContainer }
              type="button"
              style={ getComponentStyle(componentStyles.profileInfoContainer) }
              suppressHydrationWarning
            >
              <div suppressHydrationWarning>
                { nickname || '' }
              </div>
              <div className={ classes.profileInfoContainerDivider } suppressHydrationWarning />
              <img className={ classes.avatar } src={ getAvatar({ avatarUrlResize, avatarUrl }) } alt="" />
            </div>
          </ProfileInfoPopover>
        </div>
      );
    }
    return (
      <div className={ classes.signInButtonsContainer }>
        <button onClick={ showSignIn } type="button" className={ classes.signIn } style={ getComponentStyle(componentStyles.signIn) } suppressHydrationWarning>
          { t('sign_in') }
        </button>
        {renderJoinButton()}
      </div>
    );
  }

  const topBannerProps = {
    bonusInfos,
    userBonusInfos,
    userXfmProgram,
    userId,
    shouldShowMarketingBanner: matchBannerRoutes(routeKey),
    xfmFreeSetupMin: getXfmFreeSetupMin(),
    xfmDefaultMinutes: getXfmDefaultMinutes(),
    isXFMActive: isXFMActive(),
    isPrerenderMode: isPrerenderMode()
  };

  return (
    <div id="mainHeaderID" data-testid="mainHeaderID">
      <div
        data-testid="header-container"
        suppressHydrationWarning
        className={ classes.headerContainer }
        style={ getComponentStyle(componentStyles.headerContainer) }
      >
        <div className={ classes.headerContent } suppressHydrationWarning>
          <div className={ classes.contentHolder } suppressHydrationWarning>
            <HeaderLogo
              toggleDrawer={ toggleDrawer }
              sideMenuIcon={ getComponentStyle(componentStyles.sideMenuIcon) }
              badgeCount={ badgeCount }
              itemProp={ itemProp }
            />
            <div className={ classes.headerSearchAndProfileContainer }>
              <HeaderSearch />
              { renderProfileInfo() }
            </div>
          </div>
        </div>
      </div>
      <TopBanner topBannerProps={ topBannerProps } overlay={ routeKey === appRoutes.home } />
    </div>
  );
}

export default AppHeader;
